// PlanTurn: the plan half of one assistant turn: what we couldn't do, and
// what we propose to build.
//
// Rendered by BOTH the docked panel and the Home tab. That is the point: the
// two surfaces show one conversation, so a turn carrying a plan has to look
// the same wherever the owner happens to be reading it. Expanding to Home used
// to drop the plan card and the Build button entirely.
//
// Renders nothing for ordinary chat turns, so callers can drop it in beside
// their message bubble without branching.

import type { AdminChatMessage } from "@/contexts/AdminChatContext";
import FeasibilityNotice from "./FeasibilityNotice";
import PlanCard from "./PlanCard";

interface PlanTurnProps {
  message: AdminChatMessage;
  /** From useCopilotPlans. The only turn with live controls. */
  pendingPlanId: string | null;
  committingId: string | null;
  onBuild: (messageId: string) => void;
  /** Put the cursor in the composer so "Change something" is actionable. */
  onRevise: () => void;
  onOpenWorkflow: (workflowId: string) => void;
  onUseSuggestion: (text: string) => void;
}

export default function PlanTurn({
  message,
  pendingPlanId,
  committingId,
  onBuild,
  onRevise,
  onOpenWorkflow,
  onUseSuggestion,
}: PlanTurnProps) {
  const hasGaps = Boolean(message.verdict && message.gaps?.length);
  if (!hasGaps && !message.plan) return null;

  return (
    <div className="flex flex-col gap-3">
      {hasGaps && (
        <FeasibilityNotice
          verdict={message.verdict!}
          gaps={message.gaps!}
          suggestion={message.suggestion}
          onUseSuggestion={onUseSuggestion}
        />
      )}

      {message.plan && (
        <PlanCard
          plan={message.plan}
          interactive={message.id === pendingPlanId}
          committing={committingId === message.id}
          committedWorkflowId={message.committedWorkflowId}
          onBuild={() => onBuild(message.id)}
          onRevise={onRevise}
          onOpenWorkflow={onOpenWorkflow}
        />
      )}
    </div>
  );
}
